import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [sessionId] = useState(() => {
    let id = localStorage.getItem('session_id');
    if (!id) {
      id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('session_id', id);
    }
    return id;
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleConsent = useCallback(async (accepted) => {
    localStorage.setItem('cookie_consent', accepted ? 'accepted' : 'refused');
    setShowBanner(false);
    
    try {
      await axios.post(`${API}/cookie-consent`, {
        session_id: sessionId,
        accepted: accepted
      });
    } catch {
      // Silent fail - consent saved locally, API failure is non-critical
    }
  }, [sessionId]);

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-coral/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-coral" />
              </div>
              <div className="flex-1">
                <h3 className="font-nunito font-bold text-lg text-gray-900 mb-2">
                  Nous respectons votre vie privée
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Ce site utilise des cookies essentiels pour son bon fonctionnement. 
                  Aucun cookie de tracking ou publicitaire n'est utilisé. 
                  En continuant, vous acceptez notre{' '}
                  <Link to="/confidentialite" className="text-burgundy underline hover:no-underline">
                    politique de confidentialité
                  </Link>.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleConsent(true)}
                    className="px-6 py-2.5 bg-burgundy text-white rounded-full font-semibold text-sm hover:bg-burgundy-600 transition-all"
                    data-testid="cookie-accept-btn"
                  >
                    Accepter
                  </button>
                  <button
                    onClick={() => handleConsent(false)}
                    className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-full font-semibold text-sm hover:border-burgundy hover:text-burgundy transition-all"
                    data-testid="cookie-refuse-btn"
                  >
                    Refuser
                  </button>
                  <Link
                    to="/cookies"
                    className="px-6 py-2.5 text-gray-500 font-semibold text-sm hover:text-burgundy transition-colors"
                    data-testid="cookie-more-info"
                  >
                    En savoir plus
                  </Link>
                </div>
              </div>
              <button
                onClick={() => setShowBanner(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Fermer"
                data-testid="cookie-close-btn"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
